/**
 * @file main.component.ts
 */
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AuthService } from '../core/services/auth/auth.service';
import { forkJoin, Observable, Subscription, defer, of, Subject } from 'rxjs';
import {
  map,
  shareReplay,
  filter,
  take,
  switchMap,
  catchError,
  tap,
} from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MultilingualService } from '../core/services/multilingual/multilingual.service';
import {
  Router,
  NavigationStart,
  ActivatedRoute,
  NavigationEnd,
} from '@angular/router';
import { CartService } from '../services/cart/cart.service';
import { UserService } from '../services/user/user.service';
import { MemberService } from '../services/member/member.service';
import { Member } from '../interfaces/member.interface';
import { AccountBalanceResponse } from '../interfaces/account-balance-response.interface';
import { FamilyCoverageResponse } from '../interfaces/family-coverage-response.interface';
import { CmsService } from '../services/cms/cms.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UpdateProfileComponent } from './update-profile/update-profile.component';
import { AppInsightsService } from '../services/app-insights/app-insights.service';
import { AppCenterAnalytics } from '../services/app-insights/app-insights';
import { User } from '../interfaces/user.interface';
import { CMS_CONFIG_FALLBACK, COMMON_ERROR_MSG } from './main.constants';
import { DowntimeDialogComponent } from '../shared/downtime-dialog/downtime-dialog.component';
import { AppConstants } from '../app.constants';
import { MailOrderProfileEnrollmentService } from '../services/mail-order-profile-enrollment/mail-order-profile-enrollment.service';
import { UpdateMailOrderProfileDialogComponent } from '../shared/update-mail-order-profile-dialog/update-mail-order-profile-dialog.component';
import { MailOrderFamilyMember } from '../interfaces/mail-order-profile.interface';
import { PmartPatientEnrollmentFormComponent } from '../shared/mail-order-patient-enrollment-form/mail-order-patient-enrollment-form.component';
import { Link } from '../interfaces/link.interface';
import { PAGE_NAME } from '../enums/enum';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  @ViewChild('updateProfileError', { read: TemplateRef })
  updateProfileError!: TemplateRef<any>;
  @ViewChild('success', { read: TemplateRef }) successTemplate!: TemplateRef<any>;

  private readonly subscription = new Subscription();
  public toggleSideNav = false;
  public toggleSideNavSubmenu = false;
  public toggleSideNavSubmenu1 = false;

  public toggleSideNavSubmenuHelp = false;
  public toggleSideNavSubmenuMailOrder = false;
  public isCartPage = false;
  public selectedLanguage!: string;
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );

  member: Member | null = null;
  accountBalanceResponse: AccountBalanceResponse | null = null;
  familyCoverageResponse: FamilyCoverageResponse | null = null;
  user: User | null = null;

  url!: string;
  errorMsg!: string;
  errorMsgForUpdateProfile!: string;
  isError = false;
  isLoggedInMemberTermed = false;
  isLoggedInMemberFutureEffective = false;
  clientMessage!: string;
  commonBannerMessage!: string;
  clientName$: Subject<boolean> = new Subject<boolean>();
  fetchCmsConfigurations$: Subject<string> = new Subject<string>();
  links: Link[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly breakpointObserver: BreakpointObserver,
    public readonly multilingualService: MultilingualService,
    public readonly cartService: CartService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly memberService: MemberService,
    public readonly cmsService: CmsService,
    private readonly snackBar: MatSnackBar,
    public readonly active: ActivatedRoute,
    private readonly appInsightsService: AppInsightsService,
    public readonly dialog: MatDialog,
    public readonly mailOrderService: MailOrderProfileEnrollmentService
  ) {
    this.selectedLanguage = this.multilingualService.getUserLanguage();
    this.changeTextSize(AppConstants.TEXT_SIZE.SMALL);

    router.events.subscribe((o) => {
      if (o instanceof NavigationEnd) {
        this.url = o.url;
      }
    });

    this.subscription.add(
      this.clientName$
        .pipe(
          take(1),
          switchMap(() => this.getClientName())
        )
        .subscribe({
          error: () =>
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.LOGIN_SUCCESSFUL
            ),
          complete: () =>
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.LOGIN_SUCCESSFUL
            ),
        })
    );

    this.subscription.add(
      this.fetchCmsConfigurations$
        .pipe(
          take(1),
          switchMap((client) =>
            this.cmsService.loadConfigurations(client).pipe(
              take(1),
              tap((res) => {
                this.links = res.links ? res.links : [];
                // show banner message configured at client level
                if (res.clientSpecificConfig.clientBannerMessage) {
                  this.setClientMessage(
                    client,
                    res.clientSpecificConfig.clientBannerMessage
                  );
                }

                // show common banner message configured
                if (res.clientSpecificConfig.enableBannerMessage.commonHeader) {
                  this.showBannerMessage(client);
                }

                // check if mail order features are disabled for client
                if (res.clientSpecificConfig.suppressMailOrderFeatures) {
                  this.mailOrderService.disableMailOrderFeatures$.next(true);
                } else {
                  this.mailOrderService.disableMailOrderFeatures$.next(false);
                }
              })
            )
          )
        )
        .subscribe()
    );

    this.isLoggedInMemberTermed = userService.isMemberTermed;
    this.isLoggedInMemberFutureEffective = userService.isMemberFutureEffective;
  }

  ngOnInit(): void {
    this.errorMsg = this.multilingualService.langMap?.LABEL_ELIGIBILITY_FAILED;
    if (this.authService.isAuthorizedByAzureB2c) {
      this.getUserName();
    } else {
      this.getUser();
    }

    if (this.router.url.includes('cart')) {
      this.isCartPage = true;
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (event.url.includes('cart')) {
            this.isCartPage = true;
          } else {
            this.isCartPage = false;
          }
        }
      });
  }

  private getUserName(): void {
    this.memberService.getUserName(this.userService.getObjectId()).subscribe(
      (userName) => {
        this.userService.userName = userName;
        this.appInsightsService.setUsernameForAppInsights(this.userService.userName);
        this.getMemberDetails(userName);
      },
      (err) => {
        this.isError = true;
        this.errorMsg = err ? err : COMMON_ERROR_MSG;
      }
    );
  }

  private getMemberDetails(userName: string): void {
    this.memberService.getMemberDetails(userName).subscribe(
      (user) => {
        this.user = user;
        this.memberService.user = user;
        this.getUser();
      },
      (error) => {
        this.isError = true;
        this.errorMsg = error ? error : COMMON_ERROR_MSG;
      }
    );
  }

  private getSsoMemberDetails(
    eligibilityResponse: any,
    dateOfBirth?: string,
    firstName?: string,
    lastName?: string
  ): void {
    this.memberService
      .getSsoMemberDetails(
        eligibilityResponse.clientId,
        eligibilityResponse.cardId,
        dateOfBirth,
        firstName,
        lastName
      )
      .pipe(take(1))
      .subscribe(
        (user) => {
          this.user = user;
          this.memberService.user = user;
          this.updateCardId(eligibilityResponse);
        },
        (error) => {
          this.isError = true;
          this.errorMsg = error ? error : COMMON_ERROR_MSG;
        }
      );
  }

  private updateCardId(response: any) {
    // UPDATE CARD ID LOGIC
    if (this.authService.isAuthorizedByAzureB2c) {
      // NON SSO CARD ID UPDATE
      if (this.userService.userName && this.user?.cardId != response.cardId) {
        this.memberService
          .updateMemberCardId(this.userService.userName, response.cardId)
          .subscribe(
            (res) => {
              if (this.user) {
                this.user.cardId = response.cardId;
              }
              this.appInsightsService.trackPageView(
                AppCenterAnalytics.UPDATE_CARD_ID_ATTEMPT,
                { Response: res }
              );
              console.log(
                `${JSON.stringify(res)} - CARD ID UPDATED IN BACKEND.`
              );
            },
            (err) => {
              this.appInsightsService.trackPageView(
                AppCenterAnalytics.UPDATE_CARD_ID_ATTEMPT_FAILED,
                { Response: err }
              );
            }
          );
      }
    } else {
      // SSO USER CARD ID UPDATE
      if (
        this.user?.cardId != response.cardId &&
        this.user &&
        this.user.userName
      ) {
        this.memberService
          .updateMemberCardId(this.user?.userName, response.cardId)
          .subscribe(
            (res) => {
              if (this.user) {
                this.user.cardId = response.cardId;
              }
              this.appInsightsService.trackPageView(
                AppCenterAnalytics.UPDATE_CARD_ID_ATTEMPT,
                { Response: res }
              );
              console.log(
                `${JSON.stringify(res)} - CARD ID UPDATED IN BACKEND.`
              );
            },
            (err) => {
              this.appInsightsService.trackPageView(
                AppCenterAnalytics.UPDATE_CARD_ID_ATTEMPT_FAILED,
                { Response: err.error }
              );
            }
          );
      }
    }
  }

  private getClientName(): Observable<string | null> {
    if (this.userService.eligibilityVerificationResponse?.clientId) {
      return this.userService
        .fetchClientName(
          this.userService.eligibilityVerificationResponse?.clientId
        )
        .pipe(
          take(1),
          tap((res) => this.userService.setClientName(res.clientShort_Name)),
          map((res) => res.clientShort_Name)
        );
    } else {
      return of(null);
    }
  }

  private async getUser(): Promise<void> {
    let disableTermedUserAfter: number = 0;
    try {
      const result = await this.cmsService
        .getConfigurationSettings()
        .toPromise();
      disableTermedUserAfter = +result['DISABLE_TERMED_USER_AFTER'];
    } catch (ex) { }

    if (disableTermedUserAfter === 0) {
      disableTermedUserAfter = CMS_CONFIG_FALLBACK.DISABLE_TERMED_USER_AFTER;
    }
    let eligibilityAPI = this.userService.getUserEligbility();

    if (this.authService.isAuthorizedBySSO) {
      eligibilityAPI = this.userService
        .getUserEligbilitySSO(
          this.userService.getClientId(),
          this.userService.getCardId(),
          this.userService.getPersonCode(),
          this.userService.getSequenceCode()
        )
        .pipe(take(1));
    }

    this.subscription.add(
      eligibilityAPI.pipe(take(1)).subscribe(
        (response) => {
          // Update CardId Logic
          this.updateCardId(response);
          // TERMED USER LOGIC
          const eligibilityEndDate = new Date(response.eligibilityEndDate);
          const addedDisableTermUserAfter = new Date(
            eligibilityEndDate.setMonth(
              eligibilityEndDate.getMonth() + disableTermedUserAfter
            )
          );
          this.userService.eligibilityVerificationResponse = response;
          this.appInsightsService.setClientIdForAppInsights(this.userService.eligibilityVerificationResponse?.clientId || '');

          this.fetchCmsConfigurations$.next(response.clientId);
          this.clientName$.next(true);

          if (
            response &&
            response.cardId &&
            response.clientId &&
            response.personCode &&
            (response.eligibilityStatus === 'ELIGIBLE' ||
              response.eligibilityStatus === 'ELIGIBILE' ||
              (response.eligibilityStatus === 'TERMED' &&
                addedDisableTermUserAfter.getTime() > new Date().getTime()) ||
              response.eligibilityStatus === 'FUTURE_EFFECTIVE')
          ) {
            if (response.eligibilityStatus === 'TERMED') {
              this.userService.isMemberTermed = true;
              this.isLoggedInMemberTermed = true;
            }
            if (response.eligibilityStatus === 'FUTURE_EFFECTIVE') {
              this.userService.isMemberFutureEffective = true;
              this.isLoggedInMemberFutureEffective = true;
            }
            if (
              this.memberService.user &&
              !this.memberService.user.isProfileVerified &&
              (response.eligibilityStatus === 'ELIGIBLE' ||
                response.eligibilityStatus === 'ELIGIBILE')
            ) {
              const dialogRef = this.dialog.open(UpdateProfileComponent, {
                disableClose: true,
                data: false,
              });
              this.subscription.add(
                dialogRef.afterClosed().subscribe((result) => {
                  if (result.dialogStatus === 0) {
                    this.snackBar.openFromTemplate(this.successTemplate, {
                      duration: 4000,
                      verticalPosition: 'top',
                      horizontalPosition: 'center',
                    });
                  }

                  if (result.dialogStatus === 1) {
                    this.errorMsgForUpdateProfile = result.msg;
                    this.snackBar.openFromTemplate(this.updateProfileError, {
                      duration: 4000,
                      verticalPosition: 'top',
                      horizontalPosition: 'center',
                    });
                  }
                })
              );
            }
            this.getData(
              this.userService.eligibilityVerificationResponse.cardId,
              this.userService.eligibilityVerificationResponse.clientId
            );
          } else {
            this.isError = true;
            this.errorMsg =
              this.multilingualService.langMap?.LABEL_ELIGIBILITY_FAILED;
          }
        },
        (err) => {
          this.showOutagePopup();
          this.isError = true;
          this.errorMsg = err ? err : COMMON_ERROR_MSG;
        }
      )
    );
  }

  private showOutagePopup(): void {
    const defaultSettings = {
      show: false,
      message: '',
      title: '',
      is_complete_shutdown: false,
    };

    this.cmsService
      .getMaintenanceMode()
      .pipe(
        map((res) => {
          try {
            if (res && res.length > 0) {
              const configuration = res[0];
              const isMaintenance =
                new Date().valueOf() >=
                  new Date(configuration.start_date).valueOf() &&
                new Date(configuration.end_date).valueOf() >=
                  new Date().valueOf();
              // checked if maintenance and shutdown is enable or not
              if (
                configuration.display_flag &&
                configuration.is_complete_shutdown &&
                configuration.complete_shutdown_settings
              ) {
                const isCompleteShutDown =
                  new Date().valueOf() >=
                    new Date(
                      configuration.complete_shutdown_settings.start_date
                    ).valueOf() &&
                  new Date(
                    configuration.complete_shutdown_settings.end_date
                  ).valueOf() >= new Date().valueOf();
                // check current time lies between shutdown time show dialog
                if (isCompleteShutDown) {
                  return {
                    show: isCompleteShutDown,
                    message: this.getMaintenanceTexts(configuration.complete_shutdown_settings).message,
                    title: this.getMaintenanceTexts(configuration.complete_shutdown_settings).title,
                    is_complete_shutdown: true,
                  };
                } else {
                  // check current time lies between maintenance time show dialog
                  if (isMaintenance) {
                    return {
                      show: isMaintenance,
                      message: this.getMaintenanceTexts(configuration).message,
                      title: this.getMaintenanceTexts(configuration).title,
                      is_complete_shutdown: false,
                    };
                  } else {
                    return defaultSettings;
                  }
                }
                // Checked if maintenance is true and shutdown is false
              } else if (
                configuration.display_flag &&
                !configuration.is_complete_shutdown
              ) {
                if (isMaintenance) {
                  return {
                    show: isMaintenance,
                    message: this.getMaintenanceTexts(configuration).message,
                    title: this.getMaintenanceTexts(configuration).title,
                    is_complete_shutdown: false,
                  };
                } else {
                  return defaultSettings;
                }
              } else {
                return defaultSettings;
              }
            }
            return defaultSettings;
          } catch (error) {
            return defaultSettings;
          }
        }),
        switchMap(
          (res: {
            show: boolean;
            message: string;
            title: string;
            is_complete_shutdown: boolean;
          }) => {
            console.log('Maintenance mode settings', res);
            if (res.show) {
              return this.callOutagePopup(
                res.title,
                res.message,
                res.is_complete_shutdown
              ).pipe(map((res) => res));
            }
            return of(res);
          }
        ),
        catchError((err) => {
          return of(defaultSettings);
        })
      )
      .subscribe();
  }

  private getMaintenanceTexts(configuration: {
    message_en: string;
    message_es: string;
    title_en: string;
    title_es: string;
  }): { message: string; title: string } {
    if (
      this.multilingualService.selectedLang === AppConstants.LANGUAGE.SPANISH
    ) {
      return {
        message: configuration.message_es,
        title: configuration.title_es,
      };
    } else {
      return {
        message: configuration.message_en,
        title: configuration.title_en,
      };
    }
  }

  private callOutagePopup(
    title: string,
    message: string,
    outageFlag: boolean
  ): Observable<any> {
    this.appInsightsService.trackPageView(
      AppCenterAnalytics.PLANNED_OUTAGE_MSG_DISPLAYED,
      {
        data: {
          title: title,
          displayed_message: message,
          isCompleteShutdown: outageFlag,
        },
      }
    );
    const dialogRef = this.dialog.open(DowntimeDialogComponent, {
      disableClose: true,
      data: { title, message, outageFlag },
    });
    return dialogRef.afterClosed().pipe(
      take(1),
      tap(() => {
        this.appInsightsService.trackPageView(
          AppCenterAnalytics.PLANNED_OUTAGE_MSG_CLOSED
        );
      })
    );
  }

  private getData(cardId: string, clientId: string): void {
    forkJoin([
      this.memberService.familyCoverage(cardId, clientId),
      this.getAccountBalance(),
    ])
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.userService.familyCoverageResponse = res[0];
          this.userService.allFamilyCoverageResponse = { ...res[0] };
          this.familyCoverageResponse = res[0];

          if (this.userService.eligibilityVerificationResponse) {
            this.userService.member =
              this.memberService.getCurrentMember(
                this.familyCoverageResponse,
                this.userService.eligibilityVerificationResponse.personCode
              ) || null;
            this.member = this.userService.member;
          }

          this.checkToFetchDigitalIdCard(clientId);
          this.userService.familyCoverageResponse.members =
            this.userService.familyCoverageResponse.members.filter((o) =>
              this.userService.member?.allowedPhiPersonCodes.includes(
                o.personCode
              )
            );
          this.userService.accountBalanceResponse = res[1];
          this.accountBalanceResponse = res[1];

          if (this.authService.isAuthorizedBySSO) {
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.SSO_USER_LOGGED_IN
            );

            this.getSsoMemberDetails(
              this.userService.eligibilityVerificationResponse,
              this.userService.getdob(),
              this.userService.getFirstName(),
              this.userService.getLastName()
            );
          }

          // check if mail order profile is created for member
          this.showUpdateMailOrderProfileDialog();
        },
        (error) => {
          // if the API fails for any reason, the check if there is any scheduled downtime
          this.showOutagePopup();
          this.isError = true;
          this.errorMsg = error ? error : COMMON_ERROR_MSG;
        }
      );
  }

  private checkToFetchDigitalIdCard(clientId: string): void {
    // Check if Digital ID Cards are disabled for the client ID
    this.subscription.add(
      this.cmsService.loadConfigurations(clientId).pipe(take(1)).subscribe((result) => {
        const disableDigitalIds =
          result.clientSpecificConfig.disableDigitalIDCards;

        if (!disableDigitalIds) {
          this.getIdCardImage(this.userService.member);
        }
      })
    );
  }

  private showUpdateMailOrderProfileDialog() {
    this.mailOrderService
      .showUpdateProfileDialog()
      .pipe(
        take(1),
        switchMap((res) => {
          if (res) {
            // open dialog
            const dialogRef = this.dialog.open(
              UpdateMailOrderProfileDialogComponent,
              {
                height: 'auto',
                width: '500px',
                hasBackdrop: true,
                disableClose: true,
                data: {
                  loadMembersByPHI: true,
                },
              }
            );
            this.showOutagePopup();
            return dialogRef.afterClosed().pipe(
              switchMap(
                (res: {
                  action: 'update-now' | 'update-later' | null;
                  selectedMember: MailOrderFamilyMember;
                }) => {
                  // console.log('res', res);
                  return defer(() => {
                    if (res.action === 'update-now') {
                      const dialogRef2 = this.dialog.open(
                        PmartPatientEnrollmentFormComponent,
                        {
                          height: 'auto',
                          width: '60vw',
                          panelClass: 'update-profile-dialog',
                          data: {
                            member: res.selectedMember.memberProfile,
                            pageName: PAGE_NAME.HOME_UPDATE_DIALOG,
                          },
                          disableClose: true,
                        }
                      );
                      return dialogRef2.afterClosed().pipe(
                        tap(() => {
                          this.mailOrderService.hideUpdateProfileMessage$.next(
                            true
                          );
                        })
                      );
                    } else {
                      return of(false);
                    }
                  });
                }
              )
            );
          } else {
            this.showOutagePopup();
            return of(false);
          }
        })
      )
      .subscribe();
  }

  private getAccountBalance(): Observable<AccountBalanceResponse | null> {
    return defer(() => {
      if (!this.userService.isOrionUser()) {
        return this.memberService.getAccountBalance();
      } else {
        // bypass the mail order balance API for orion users
        return of({
          currentBalance: '',
          message: '',
        });
      }
    });
  }

  private getIdCardImage(member: Member | null): void {
    if (!member) {
      this.member = this.userService.member;
      return;
    }
    this.memberService
      .temporaryIdCard(member.personCode)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.createImageFromBlob(member, res);
        },
        (error) => {
          this.member = this.userService.member;
          if (this.member) {
            this.member.cardImageError = error ? error : COMMON_ERROR_MSG;
          }
        }
      );
  }

  private createImageFromBlob(member: Member, image: Blob): void {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        member.cardImage = reader.result as string;
        this.member = this.userService.member;
      },
      false
    );

    if (image) {
      if (image.type !== 'application/pdf') {
        reader.readAsDataURL(image);
      }
    }
  }

  public onLogoutClick(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent);
    this.subscription.add(
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((result) => {
          if (result) {
            this.logout();
          }
        })
    );
  }

  logout(): void {
    this.authService.logout();
  }

  public toggleSideNavfn(): void {
    this.toggleSideNav = !this.toggleSideNav;
  }

  public toggleSideNavSubmenufn(): void {
    this.toggleSideNavSubmenu = !this.toggleSideNavSubmenu;
    this.toggleSideNavSubmenuHelp = false;
    this.toggleSideNavSubmenuMailOrder = false;
    this.toggleSideNavSubmenu1 = false;
  }

  public toggleSideNavSubmenuHelpfn(): void {
    this.toggleSideNavSubmenuHelp = !this.toggleSideNavSubmenuHelp;
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenuMailOrder = false;
    this.toggleSideNavSubmenu1 = false;
  }

  public toggleSideNavSubmenuReviewfn(): void {
    this.toggleSideNavSubmenu1 = !this.toggleSideNavSubmenu1;
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenuHelp = false;
    this.toggleSideNavSubmenuMailOrder = false;
  }

  public toggleSideNavSubmenuMailfn(): void {
    this.toggleSideNavSubmenuMailOrder = !this.toggleSideNavSubmenuMailOrder;
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenu1 = false;
    this.toggleSideNavSubmenuHelp = false;
  }

  public changeLang(lang: string): void {
    this.multilingualService.getLangMap(lang);
    this.multilingualService.langMapFailureObservable.subscribe((res) => {
      this.selectedLanguage = this.multilingualService.getUserLanguage();
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      });
    });
  }

  public closeAll(): void {
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenuHelp = false;
  }

  aboutUsClicked(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.ABOUT_US_VIEWED);
  }

  settingsOpenUpdateProfile(): void {
    const dialogRef = this.dialog.open(UpdateProfileComponent, { data: true });
    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result.dialogStatus === 0) {
          if (this.authService.isAuthorizedByAzureB2c) {
            this.getUserName();
          } else {
            this.getSsoMemberDetails(
              this.userService.eligibilityVerificationResponse,
              this.userService.member?.dateOfBirth.toString(),
              this.userService.member?.firstName,
              this.userService.member?.lastName
            );
          }
          this.snackBar.openFromTemplate(this.successTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
        }

        if (result.dialogStatus === 1) {
          this.errorMsgForUpdateProfile = result.msg;
          this.snackBar.openFromTemplate(this.updateProfileError, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
        }
      })
    );
  }

  public changeTextSize(textSizeValue: string) {
    let fontSize: string;
    this.multilingualService.selectedTextSize = textSizeValue;

    switch (textSizeValue) {
      case AppConstants.TEXT_SIZE.MEDIUM: {
        fontSize = '18px';
        break;
      }
      case AppConstants.TEXT_SIZE.LARGE: {
        fontSize = '20px';
        break;
      }
      default: {
        fontSize = '16px';
        break;
      }
    }
    document.getElementsByTagName('html')[0].style.fontSize = fontSize;
    const bodyClasses = document.getElementsByTagName('body')[0].classList;
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.LARGE}-text-size`);
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.MEDIUM}-text-size`);
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.SMALL}-text-size`);

    bodyClasses.add(`${textSizeValue}-text-size`);
  }

  public isMigratedMember(): boolean {
    return this.userService.isMigratedMember();
  }

  public isFutureEffective(): boolean {
    return this.userService.isFutureEffectiveUser();
  }

  private setClientMessage(clientId: string, bannerMessage: string): void {
    this.clientMessage = bannerMessage;
    const data = {
      clientId: clientId,
      message: 'bannerMessage',
    };
    this.appInsightsService.trackPageView(
      `${AppCenterAnalytics.HOME_SCREEN_CLIENT_MESSAGE_DISPLAYED}`,
      { data: data }
    );
  }

  private showBannerMessage(clientId: string): void {
    const message = this.multilingualService?.langMap?.LABEL_COMMON_HEADER_BANNER_MESSAGE;
    const data = {
      screen: "COMMON HEADER",
      clientId: clientId,
      message,
    };
    this.commonBannerMessage = 'LABEL_COMMON_HEADER_BANNER_MESSAGE';
    this.appInsightsService.trackPageView(
      `${AppCenterAnalytics.BANNER_MESSAGE_DISPLAYED}`,
      { data: data }
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
